import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const generateInvoicePDF = (invoice, items, siteData) => {
    // A4 size: 210 x 297 mm
    const doc = new jsPDF('p', 'mm', 'a4');
    
    // Configurable Society Details (Fallback to defaults if not provided in siteData)
    const societyName = siteData?.societyName || 'CENTRUM IT PARK CO.OP PREMISES SOCIETY LIMITED';
    const societyAddress1 = siteData?.address1 || '(Regd. No. : TNA/(TNA)/GNL/(0)/3010/Year-2020)';
    const societyAddress2 = siteData?.address2 || 'C-3, Centrum Park, Road No.1, Thane (W)-400604';
    const societyGst = siteData?.gstNo || '27AAFAC0380F1Z2';
    
    // Fonts and Layout Setup
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    
    // Header
    const pageWidth = doc.internal.pageSize.width;
    doc.text(societyName, pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(societyAddress1, pageWidth / 2, 21, { align: 'center' });
    doc.text(societyAddress2, pageWidth / 2, 26, { align: 'center' });
    
    doc.setFont("helvetica", "bold");
    doc.text(`GST No. : ${societyGst}`, pageWidth / 2, 31, { align: 'center' });
    
    doc.text('Ledger Monthly Summary', pageWidth / 2, 37, { align: 'center' });
    
    // Sub-header Line
    doc.line(10, 40, pageWidth - 10, 40);
    
    // Two-column layout for details
    doc.setFontSize(10);
    
    // Left Column
    doc.setFont("helvetica", "bold");
    doc.text("Name", 12, 46);
    doc.text("GST No.", 12, 52);
    doc.text("Unit / Shop No", 12, 58);
    doc.text("Area", 12, 64);
    doc.text("Car Parking Slot", 12, 70);
    
    doc.setFont("helvetica", "normal");
    doc.text(`: ${invoice.UnitAdminFirstName} ${invoice.UnitAdminLastName}`, 45, 46);
    doc.text(`: ${invoice.GSTNo || 'N/A'}`, 45, 52);
    doc.text(`: ${invoice.UnitNumber || 'N/A'}`, 45, 58);
    doc.text(`: ${invoice.Area || 'N/A'} Sq. Ft.`, 45, 64);
    doc.text(`: ${invoice.CarParkingSlot || ''}`, 45, 70);
    
    // Right Column
    doc.setFont("helvetica", "bold");
    doc.text("Bill No.", 140, 46);
    doc.text("Bill Date", 140, 52);
    doc.text("Month", 140, 58);
    doc.text("Due Date", 140, 64);
    
    doc.setFont("helvetica", "normal");
    const billDate = invoice.CreatedAt ? new Date(invoice.CreatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : 'N/A';
    const dueDate = invoice.DueDate ? new Date(invoice.DueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : 'N/A';

    doc.text(`: ${invoice.InvoiceNumber}`, 160, 46);
    doc.text(`: ${billDate}`, 160, 52);
    doc.text(`: ${invoice.BillingMonth || 'N/A'}`, 160, 58);
    doc.text(`: ${dueDate}`, 160, 64);
    
    // Table 1: Line Items
    const tableHeaders = [
        ['Sr. No.', 'Particulars', 'SAC Code', 'GST Applicable', 'Amount']
    ];
    
    // Filter and map line items from DB
    const nonTaxItems = (items || []).filter(item => item.ItemType !== 'TAX');
    const taxItems = (items || []).filter(item => item.ItemType === 'TAX');
    
    let tableData = nonTaxItems.map((item, index) => [
        index + 1,
        item.Description,
        item.SacCode || '',
        item.IsGstApplicable ? 'Yes' : 'No',
        parseFloat(item.TotalAmount).toFixed(2)
    ]);
    
    const startIndexForTaxes = tableData.length + 1;
    taxItems.forEach((taxItem, index) => {
        tableData.push([
            startIndexForTaxes + index,
            taxItem.Description,
            '',
            'No',
            parseFloat(taxItem.TotalAmount).toFixed(2)
        ]);
    });
    
    autoTable(doc, {
        startY: 75,
        head: tableHeaders,
        body: tableData,
        theme: 'grid',
        headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            fontStyle: 'bold',
            halign: 'center'
        },
        styles: {
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            textColor: [0, 0, 0],
            fontSize: 9
        },
        columnStyles: {
            0: { halign: 'center', cellWidth: 20 },
            1: { halign: 'left' },
            2: { halign: 'center', cellWidth: 25 },
            3: { halign: 'center', cellWidth: 30 },
            4: { halign: 'right', cellWidth: 30 }
        }
    });
    
    let finalY = doc.lastAutoTable.finalY;
    
    // Net Charges / Total row
    autoTable(doc, {
        startY: finalY,
        body: [
            ['Net Charges', '', '', '', parseFloat(invoice.TotalAmount).toFixed(2)],
            ['Gross Total (Balance as on bill date)', '', '', '', parseFloat(invoice.TotalAmount).toFixed(2)]
        ],
        theme: 'grid',
        styles: {
            lineColor: [0, 0, 0],
            lineWidth: 0.1,
            textColor: [0, 0, 0],
            fontSize: 10,
            fontStyle: 'bold'
        },
        columnStyles: {
            0: { halign: 'left' },
            4: { halign: 'right', cellWidth: 30 }
        }
    });
    
    finalY = doc.lastAutoTable.finalY;
    
    // Bank Details Section
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Bank Details for Net Banking :", 12, finalY + 6);
    doc.setFont("helvetica", "normal");
    doc.text(siteData?.societyName || 'Centrum IT Park Co.Op Premises Society Limited', 60, finalY + 6);
    
    doc.text("Saving Account No. :", 12, finalY + 11);
    doc.setFont("helvetica", "bold");
    doc.text(siteData?.accountNo || '43927260912', 45, finalY + 11);
    
    doc.setFont("helvetica", "normal");
    doc.text("IFSC :", 100, finalY + 11);
    doc.setFont("helvetica", "bold");
    doc.text(siteData?.ifscCode || 'SBIN0061707', 110, finalY + 11);
    
    doc.setFont("helvetica", "normal");
    doc.text("Bank's Name :", 12, finalY + 16);
    doc.setFont("helvetica", "bold");
    doc.text(siteData?.bankName || 'State Bank of India', 35, finalY + 16);
    
    doc.setFont("helvetica", "normal");
    doc.text("Branch :", 100, finalY + 16);
    doc.setFont("helvetica", "bold");
    doc.text(siteData?.branchName || 'Wagle Circle(61707)', 115, finalY + 16);
    
    // Terms & Conditions Line Box
    doc.line(10, finalY + 20, pageWidth - 10, finalY + 20); // Top line
    
    doc.setFontSize(8);
    doc.text("Terms & Conditions :", 12, finalY + 25);
    doc.setFont("helvetica", "normal");
    doc.text("Payment beyond due date will attract penal charges @ 21% P.A. simple interest from due date till fully paid.", 12, finalY + 29);
    doc.text(`You are requested to issue cheque in favour of "${siteData?.societyName || 'Centrum IT Park Co-Operative Premises Society Ltd'}"`, 12, finalY + 33);
    doc.text("Incase of discrepancy in this bill inform the society within 7 days, else will be assumed to be accurate.", 12, finalY + 37);
    doc.text("Please write Unit No. and Mobile No. on back side of the cheque.", 12, finalY + 41);
    doc.text("This is computer generated Invoice and therefore signature is not required.", 12, finalY + 45);
    doc.text("All Receipts will be issued subject to clearance of Cheques/RTGS/NEFT only.", 12, finalY + 49);
    
    doc.line(10, finalY + 52, pageWidth - 10, finalY + 52); // Bottom line
    
    // Save PDF
    doc.save(`${invoice.InvoiceNumber}.pdf`);
};
